import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Switch } from '../../../components/ui/Switch.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { brandsApi } from '../../../api/brands.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

const EMPTY = { name: '', slug: '', description: '', website: '', active: true };

function slugify(s) {
  return String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);
}

export function BrandFormModal({ open, onClose, initial }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, setValue, watch, setError, formState: { errors } } = useForm({ defaultValues: EMPTY });
  const active = watch('active');

  useEffect(() => {
    if (!open) return;
    reset(initial ? {
      name: initial.name || '',
      slug: initial.slug || '',
      description: initial.description || '',
      website: initial.website || '',
      active: initial.active !== false
    } : EMPTY);
  }, [open, initial, reset]);

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        name: values.name,
        slug: values.slug || slugify(values.name),
        description: values.description || undefined,
        website: values.website || undefined,
        active: values.active
      };
      return editing ? brandsApi.update(initial._id, payload) : brandsApi.create(payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Brand aggiornato.' : 'Brand creato.');
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? `Modifica ${initial?.name}` : 'Nuovo brand'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="brand-form" type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="brand-form" className="flex flex-col gap-4" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <Input
          label="Nome"
          error={errors.name?.message}
          {...register('name', { required: 'Nome richiesto', onChange: (e) => { if (!editing) setValue('slug', slugify(e.target.value)); } })}
        />
        <Input label="Slug" hint="Identificativo url-safe, generato dal nome." error={errors.slug?.message} {...register('slug')} />
        <Input label="Sito web" placeholder="https://..." error={errors.website?.message} {...register('website')} />
        <Textarea label="Descrizione" rows={3} {...register('description')} />
        <label className="flex items-center justify-between gap-3">
          <span className="text-sm text-ink-muted">Attivo</span>
          <Switch checked={active} onChange={(v) => setValue('active', v)} />
        </label>
      </form>
    </Modal>
  );
}
